import { ReactNode } from "react";
import { Stack, Chip } from "@mui/material";
import { IMatch } from "../../types/types";
import { MatchSupplementGaps, matchHasSupplementGaps } from "../../utils/gameSupplementHelpers";
import { MATCH_STATUS_LABELS, normalizeMatchStatus } from "./matchStatusDisplay";

export type MatchTableSortKey = keyof IMatch | "gameIds" | "missingData";

export interface MatchTableColumn {
  id: string;
  header: string;
  sortKey: MatchTableSortKey;
  width: string;
  align?: "left" | "center" | "right";
  render: (match: IMatch, gameIds: number[], supplementGaps?: MatchSupplementGaps) => ReactNode;
}

const truncate = (value: string, maxLength: number) =>
  value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;

const textOrDash = (value?: string | null): string => value?.trim() || "—";

const adminMatchField = (
  match: IMatch,
  key: "teamOneName" | "teamTwoName",
): string | null | undefined =>
  (match as IMatch & Record<typeof key, string | null | undefined>)[key];

export const formatAdminMatchTime = (time?: string | null): string => {
  if (!time) return "—";
  const parsed = new Date(time);
  if (Number.isNaN(parsed.getTime())) return time;

  return new Intl.DateTimeFormat("fi-FI", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

export const formatAdminMatchType = (matchType?: string): string => {
  if (!matchType) return "—";
  const trimmed = matchType.trim();
  const weekMatch = /^FPL\s+Viikko\s+(\d+)$/i.exec(trimmed);
  if (weekMatch) return `Vko ${weekMatch[1]}`;
  if (/^playoff$/i.test(trimmed)) return "PO";
  if (/^ylösalas$/i.test(trimmed)) return "Ylös";
  return truncate(trimmed, 10);
};

export const formatAdminMatchup = (match: IMatch): string => {
  return `${textOrDash(adminMatchField(match, "teamOneName"))} vs ${textOrDash(adminMatchField(match, "teamTwoName"))}`;
};

export const formatAdminScore = (match: IMatch): string => {
  const hasScore =
    match.status === "FINISHED" ||
    match.status === "COMPLETED" ||
    match.status === "LIVE" ||
    match.teamOneScore > 0 ||
    match.teamTwoScore > 0;

  if (!hasScore) return "—";
  return `${String(match.teamOneScore)}–${String(match.teamTwoScore)}`;
};

export const formatAdminMatchStatus = (status?: string): string =>
  MATCH_STATUS_LABELS[normalizeMatchStatus(status)];

export const formatAdminGameIds = (gameIds: number[]): string => {
  if (!gameIds.length) return "—";
  if (gameIds.length <= 3) return gameIds.join(", ");
  return `${gameIds.slice(0, 3).join(", ")} +${String(gameIds.length - 3)}`;
};

const missingChipSx = {
  height: 22,
  fontSize: "0.68rem",
  "& .MuiChip-label": { px: 0.75 },
};

export const formatAdminMissingData = (
  match: IMatch,
  gameIds: number[],
  supplementGaps?: MatchSupplementGaps,
): ReactNode => {
  const isFinished = normalizeMatchStatus(match.status) === "FINISHED";
  if (!isFinished || gameIds.length === 0) return "—";
  if (!supplementGaps || !matchHasSupplementGaps(supplementGaps)) return "—";

  const labels: Array<{ key: keyof MatchSupplementGaps; label: string }> = [
    { key: "dragons", label: "Dragonit" },
    { key: "bans", label: "Bännit" },
    { key: "csAt15", label: "CS@15" },
  ];

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
      {labels
        .filter(({ key }) => supplementGaps[key])
        .map(({ key, label }) => (
          <Chip
            key={key}
            label={label}
            size="small"
            color="warning"
            variant="outlined"
            sx={missingChipSx}
          />
        ))}
    </Stack>
  );
};

export const MATCH_TABLE_COLUMNS: MatchTableColumn[] = [
  {
    id: "id",
    header: "ID",
    sortKey: "id",
    width: "5%",
    align: "right",
    render: (match) => String(match.id),
  },
  {
    id: "time",
    header: "Aika",
    sortKey: "time",
    width: "12%",
    render: (match) => formatAdminMatchTime(match.time),
  },
  {
    id: "matchup",
    header: "Ottelu",
    sortKey: "teamOneName",
    width: "24%",
    render: (match) => truncate(formatAdminMatchup(match), 44),
  },
  {
    id: "score",
    header: "Tulos",
    sortKey: "teamOneScore",
    width: "7%",
    align: "center",
    render: (match) => formatAdminScore(match),
  },
  {
    id: "matchType",
    header: "Tyyppi",
    sortKey: "matchType",
    width: "10%",
    render: (match) => formatAdminMatchType(match.matchType),
  },
  {
    id: "status",
    header: "Tila",
    sortKey: "status",
    width: "9%",
    render: (match) => formatAdminMatchStatus(match.status),
  },
  {
    id: "gameIds",
    header: "Pelit",
    sortKey: "id",
    width: "12%",
    render: (_match, gameIds) => formatAdminGameIds(gameIds),
  },
  {
    id: "missingData",
    header: "Puuttuvaa",
    sortKey: "missingData",
    width: "14%",
    render: (match, gameIds, supplementGaps) =>
      formatAdminMissingData(match, gameIds, supplementGaps),
  },
];
